<template>
	<div>
        <div class="flex justify-between mb-5 border-b border-gray-300 pb-2">
            <div class="flex items-center justify-between w-full">
                <h1 class="module-header">Effective Rent Calculator</h1>
            </div>
        </div>
		<h2 class="text-base mb-6">Calculate 1st Year Effective Rent</h2>
        <div class="flex">
        	<div :class="fromReatlas ? 'w-1/2 mr-6' : 'w-1/3 mr-16'">
				<div class="w-full">
					<h3 class="text-sm text-blue-600">Enter Requirements</h3>
					<table class="w-full shadow-none">
						<tbody>
							<tr class="border-b border-green-600">
								<td class="text-sm font-medium py-6 pl-2">Usable Sq. Ft. Req'd</td>
								<td class="text-right">
									<number-input v-model="usableSquareFeetRequired" classes="text-sm text-right"></number-input>
								</td>
							</tr>
							<tr class="border-b border-green-600">
								<td class="text-sm font-medium py-6 pl-2">Est. Loss Factor (%)</td>
								<td class="text-right">
									<percent-input v-model="estimatedLossFactor"></percent-input>
								</td>
							</tr>
							<tr class="border-b border-green-600">
								<td class="text-sm font-medium py-6 pl-2">
									<p class="text-sm">Base Rent</p>
									<p class="text-xs">PSF $</p>
								</td>
								<td class="text-right">
									<currency-input v-model="baseRent" is-decimal></currency-input>
								</td>
							</tr>
							<tr class="border-b border-green-600">
								<td class="font-medium py-3 pl-2 leading-normal">
									<p class="text-sm">Work Required</p>
									<p class="text-xs">Total $</p>
								</td>
								<td class="text-right">
									<currency-input v-model="workRequiredTotal"></currency-input>
								</td>
							</tr>
							<tr class="border-b border-green-600">
								<td class="font-medium py-3 pl-2 leading-normal">
									<p class="text-sm">Landlord Contribution</p>
									<p class="text-xs">PRSF $</p>
								</td>
								<td class="text-right">
									<currency-input v-model="landlordContributionPRSF" is-decimal></currency-input>
								</td>
							</tr>
							<tr class="border-b border-green-600">
								<td class="font-medium py-3 pl-2 leading-normal">
									<p class="text-sm">Lease Term</p>
									<p class="text-xs">Years</p>
								</td>
								<td class="text-right">
									<input type="text" size="10" class="form-input text-right" v-model="leaseTermYears">
								</td>
							</tr>
							<tr class="border-b border-green-600">
								<td class="font-medium py-3 pl-2 leading-normal">
									<p class="text-sm">Free Rent</p>
									<p class="text-xs">Months</p>
								</td>
								<td class="text-right">
									<input type="text" size="10" class="form-input text-right" v-model="freeRentMonths">
								</td>
							</tr>
							<tr class="border-b border-green-600">
								<td class="text-sm font-medium py-6 pl-2">Escalation (%)</td>
								<td class="text-right">
									<percent-input v-model="escalationPercentage"></percent-input>
								</td>
							</tr>
							<tr class="border-b border-green-600">
								<td class="text-sm font-medium py-6 pl-2">Interest for Amortization (%)</td>
								<td class="text-right">
									<percent-input v-model="interestForAmortizationPercentage"></percent-input>
								</td>
							</tr>
						</tbody>
					</table>
					<button @click="save" class="btn btn-green w-full mt-4">Save</button>
				</div>
			</div>

			<div :class="fromReatlas ? 'w-1/2' : 'w-1/3'">
				<h3 class="text-sm text-blue-600">Effective Rent Data</h3>
				<table class="w-full shadow-none">
					<tbody>
						<tr class="border-b border-green-600">
							<td class="text-sm font-medium py-6 pl-2">Rentable Sq. Ft.</td>
							<td class="text-right text-lg">{{ rentableSquareFeet | number }}</td>
						</tr>
						<tr class="border-b border-green-600">
							<td class="font-medium py-3 pl-2 leading-normal">
								<p class="text-sm">Work Required</p>
								<p class="text-xs">PUSF $</p>
							</td>
							<td :data-precision="2" :data-originalvalue="workRequiredPUSF" class="is-currency text-right text-lg">{{ formatCurrency(workRequiredPUSF, 2) }}</td>
						</tr>
						<tr class="border-b border-green-600">
							<td class="font-medium py-3 pl-2 leading-normal">
								<p class="text-sm">Est. Work Required</p>
								<p class="text-xs">PRSF $</p>
							</td>
							<td :data-precision="2" :data-originalvalue="workRequiredPRSF" class="is-currency text-right text-lg">{{ formatCurrency(workRequiredPRSF, 2) }}</td>
						</tr>
						<tr class="border-b border-green-600">
							<td class="font-medium py-3 pl-2 leading-normal">
								<p class="text-sm">Est. Work to Amortize</p>
								<p class="text-xs">PRSF $</p>
							</td>
							<td :data-precision="2" :data-originalvalue="workToAmortizePRSF" class="is-currency text-right text-lg">{{ formatCurrency(workToAmortizePRSF, 2) }}</td>
						</tr>
						<tr class="border-b border-green-600">
							<td class="text-sm font-medium py-6 pl-2">Total Work to Amortize</td>
							<td :data-precision="0" :data-originalvalue="workToAmortizeTotal" class="is-currency text-right text-lg">{{ formatCurrency(workToAmortizeTotal) }}</td>
						</tr>
						<tr class="border-b border-green-600">
							<td class="font-medium py-3 pl-2 leading-normal">
								<p class="text-sm">Work to Amortize</p>
								<p class="text-xs">PSF $</p>
							</td>
							<td :data-precision="2" :data-originalvalue="workToAmortizePSF" class="is-currency text-right text-lg">{{ formatCurrency(workToAmortizePSF, 2) }}</td>
						</tr>
						<tr class="border-b border-green-600">
							<td class="text-sm font-medium py-6 pl-2">Base Rent + Work</td>
							<td :data-precision="2" :data-originalvalue="baseRentAndWork" class="is-currency text-right text-lg">{{ formatCurrency(baseRentAndWork, 2) }}</td>
						</tr>
						<tr class="border-b border-green-600">
							<td class="font-medium py-3 pl-2 leading-normal">
								<p class="text-sm">Free Rent</p>
								<p class="text-xs">PSF $</p>
							</td>
							<td v-if="freeRentPSF > 0" :data-precision="2" :data-originalvalue="freeRentPSF" class="is-currency text-right text-red text-lg">({{ formatCurrency(freeRentPSF, 2) }})</td>
							<td v-if="freeRentPSF == 0" class="text-right text-red text-lg"></td>
						</tr>
						<tr>
							<td class="bg-green-500 text-white text-sm font-semibold py-6 pl-2">Effective Rent</td>
							<td :data-precision="2" :data-originalvalue="effectiveRentPSF" class="is-currency bg-green-500 text-lg text-white text-right font-semibold">{{ formatCurrency(effectiveRentPSF, 2) }}</td>
						</tr>
						<tr>
							<td class="bg-green-500 text-white text-sm font-semibold py-6 pl-2">1st Year's Effective Rent</td>
							<td :data-precision="0" :data-originalvalue="effectiveRentYear" class="is-currency bg-green-500 text-lg text-white text-right font-semibold">{{ formatCurrency(effectiveRentYear) }}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		props: {
			projectId: {
				type: Number,
				required: true
			},

			data: {
				type: [Object, Array],
				required: true
			}, 

            fromReatlas: {
                type: Boolean,
                default: false
            }
		},

		data() {
			return {
				workRequiredTotal: this.data.work_required_total,
				landlordContributionPRSF: this.data.landlord_contribution_prsf,
				usableSquareFeetRequired: this.data.usable_square_feet_required,
				estimatedLossFactor: this.data.estimated_loss_factor_percentage,
				baseRent: this.data.base_rent,
				leaseTermYears: this.data.lease_term_years,
				freeRentMonths: this.data.free_rent_months,
				escalationPercentage: this.data.escalation_percentage,
				interestForAmortizationPercentage: this.data.interest_amortization_percentage,
			}
		},

		computed: {
			leaseTermMonths() {
				return this.leaseTermYears * 12;
			},

			lossFactor() {
				return parseInt(this.estimatedLossFactor) / 100;
			},

			rentableSquareFeet() {
				const usableSquareFeetRequired = Number.prototype.cleanseInteger(this.usableSquareFeetRequired);

				return isNaN(usableSquareFeetRequired) || isNaN(this.lossFactor)
						? 0
						: parseInt(Math.round(usableSquareFeetRequired/(1-this.lossFactor)));
			},

			workRequiredPUSF() {
				const usableSquareFeetRequired = Number.prototype.cleanseInteger(this.usableSquareFeetRequired);
				const workRequiredTotal = Number.prototype.cleanseInteger(this.workRequiredTotal);

				return isNaN(usableSquareFeetRequired) || isNaN(workRequiredTotal)
						? 0
						: parseFloat((workRequiredTotal/usableSquareFeetRequired).toFixed(2) * 100);
			},

			workRequiredPRSF() {
				const workRequiredTotal = Number.prototype.cleanseInteger(this.workRequiredTotal);

				return isNaN(this.rentableSquareFeet) || isNaN(workRequiredTotal)
						? 0
						: parseFloat((workRequiredTotal/this.rentableSquareFeet).toFixed(2) * 100);
			},

			workToAmortizePRSF() {
				const landlordContributionPRSF = Number.prototype.cleanseFloat(this.landlordContributionPRSF);

				return isNaN(this.workRequiredPRSF) || isNaN(landlordContributionPRSF)
						? 0
						: parseFloat(Math.abs((this.workRequiredPRSF-(landlordContributionPRSF * 100)).toFixed(2)));
			},

			workToAmortizeTotal() {
				return isNaN(this.workToAmortizePRSF) || isNaN(this.rentableSquareFeet)
						? 0
						: parseFloat(Math.round(this.workToAmortizePRSF*this.rentableSquareFeet));
			},

			workToAmortizePSF() {
				if (this.rentableSquareFeet == 0 || this.workToAmortizeTotal == 0 || isNaN(this.leaseTermMonths)) {
					return 0;
				}

				const freeRentMonths = isNaN(this.freeRentMonths) ? 0 : parseInt(this.freeRentMonths);

				const pmt = Number.prototype.pmt(this.workToAmortizeTotal / 100, this.interestForAmortizationPercentage, this.leaseTermMonths + freeRentMonths);
				
				return parseFloat(((pmt*12) / this.rentableSquareFeet).toFixed(2) * 100);
			},

			baseRentAndWork() {
				const baseRent = Number.prototype.cleanseFloat(this.baseRent);

				return isNaN(baseRent) || isNaN(this.workToAmortizePSF)
						? 0
						: parseFloat(((baseRent * 100) + this.workToAmortizePSF).toFixed(2));
			},

			freeRentPSF() {
				const baseRent = Number.prototype.cleanseFloat(this.baseRent);

				return isNaN(this.leaseTermMonths)
						? 0
						: parseFloat(((this.freeRentMonths/(this.leaseTermMonths+parseInt(this.freeRentMonths)))*(baseRent)) * 100)
			},

			effectiveRentPSF() {
				return parseFloat(this.baseRentAndWork - this.freeRentPSF);
			},

			effectiveRentYear() {
				return parseFloat((this.effectiveRentPSF * this.rentableSquareFeet).toFixed(2));
			}
		},
		methods: {
            save() {
                axios.post('/projects/' + this.projectId + '/comps/' + this.data.comp_id + '/effective-rent-calculation', {
                	comp_id: this.data.comp_id,
		            usable_square_feet_required: Number.prototype.cleanseInteger(this.usableSquareFeetRequired),
		            rentable_square_feet: this.rentableSquareFeet,
		            estimated_loss_factor_percentage: Number.prototype.cleanseFloat(this.estimatedLossFactor),
		            base_rent: this.baseRent,
		            lease_term_years: this.leaseTermYears,
		            free_rent_months: this.freeRentMonths,
		            free_rent_psf: this.freeRentPSF,
		            interest_amortization_percentage: Number.prototype.cleanseFloat(this.interestForAmortizationPercentage),
		            escalation_percentage: Number.prototype.cleanseFloat(this.escalationPercentage),
		            landlord_contribution_prsf: this.landlordContributionPRSF,
		            work_required_total: Number.prototype.cleanseInteger(this.workRequiredTotal),
		            work_required_pusf: this.workRequiredPUSF,
		            estimated_work_required_prsf: this.workRequiredPRSF,
		            estimated_work_amortize_prsf: this.workToAmortizePRSF,
		            total_work_to_amortize: this.workToAmortizeTotal,
		            work_amortize_psf: this.workToAmortizePSF,
		            base_rent_plus_work: this.baseRentAndWork,
		            effective_rent_psf: this.effectiveRentPSF,
		            effective_rent_total: this.effectiveRentYear
                }).then((response) => {
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    }); 
                }).catch((response) => {
                    console.error(response);

                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: response.data.message
                    }); 
                });
            },
		},
	}
</script>